import { z } from 'zod';
import type { ToolReadingDto } from './gaps';
import { DIFF_CRITERIA, diffRated, diffSum, type DiffCriterion, type Diferenciais } from './strategy';

/* ─────────────────────────────────────────────────────────────────────
   Diferenciais Competitivos — a escala da trinca, as contas por linha e
   por coluna, e a leitura do agente: a força real de cada diferencial,
   dada pelas notas da organização e pelas evidências que ela anexa.
   ───────────────────────────────────────────────────────────────────── */

export type Tier = 'forte' | 'moderado' | 'fraco';
export const TIER_LABEL: Record<Tier, string> = {
  forte: 'diferencial competitivo forte', moderado: 'possível diferencial', fraco: 'atributo comum',
};
export const TIER_WORD: Record<Tier, string> = { forte: 'Forte', moderado: 'Moderado', fraco: 'Fraco' };

/** Uma linha: três notas de 1 a 5 (3 a 15 pontos). */
export const rowTier = (sum: number): Tier => (sum >= 12 ? 'forte' : sum >= 8 ? 'moderado' : 'fraco');
/** Uma coluna: uma nota por diferencial (n a 5n). Com três diferenciais: 3–7, 8–11, 12–15. */
export const colTier = (sum: number, n: number): Tier =>
  (sum > Math.floor((11 * n) / 3) ? 'forte' : sum > Math.floor((7 * n) / 3) ? 'moderado' : 'fraco');
/** O total: 3n a 15n. Com três diferenciais: 9–21, 22–33, 34–45. */
export const totalTier = (sum: number, n: number): Tier => (sum > 11 * n ? 'forte' : sum > 7 * n ? 'moderado' : 'fraco');

/** As faixas da legenda, para quantos diferenciais houver. */
export function diffScale(n: number) {
  const k = Math.max(1, n);
  return {
    col: [
      { tier: 'fraco' as Tier, from: k, to: Math.floor((7 * k) / 3) },
      { tier: 'moderado' as Tier, from: Math.floor((7 * k) / 3) + 1, to: Math.floor((11 * k) / 3) },
      { tier: 'forte' as Tier, from: Math.floor((11 * k) / 3) + 1, to: 5 * k },
    ],
    total: [
      { tier: 'fraco' as Tier, from: 3 * k, to: 7 * k },
      { tier: 'moderado' as Tier, from: 7 * k + 1, to: 11 * k },
      { tier: 'forte' as Tier, from: 11 * k + 1, to: 15 * k },
    ],
  };
}

type Trinca = Record<DiffCriterion, number>;

/** As contas da tabela: por linha, por coluna e o total — da organização e, se houver, da IA. */
export function diffMetrics(c: Diferenciais, reading?: DiferenciaisReading | null) {
  const rated = c.items.filter(d => d.label.trim() || diffRated(d));
  const n = rated.length;
  const ia = new Map((reading?.items ?? []).map(x => [x.id, x]));
  const rows = rated.map(d => {
    const r = ia.get(d.id ?? '');
    const iaT: Trinca | null = r ? { real: r.real, distinct: r.distinct, hard: r.hard } : null;
    return {
      id: d.id ?? '', label: d.label, files: d.files,
      user: { real: d.real, distinct: d.distinct, hard: d.hard, sum: diffSum(d), tier: rowTier(diffSum(d)) },
      ia: iaT ? { ...iaT, sum: diffSum(iaT), tier: rowTier(diffSum(iaT)), leitura: r!.leitura } : null,
    };
  });
  const col = (who: 'user' | 'ia', k: DiffCriterion) => rows.reduce((s, r) => s + (r[who]?.[k] ?? 0), 0);
  const cols = (who: 'user' | 'ia') => Object.fromEntries(DIFF_CRITERIA.map(({ key }) => {
    const sum = col(who, key);
    return [key, { sum, tier: colTier(sum, n) }];
  })) as Record<DiffCriterion, { sum: number; tier: Tier }>;
  const userTotal = rows.reduce((s, r) => s + r.user.sum, 0);
  const hasIa = rows.some(r => r.ia);
  const iaTotal = rows.reduce((s, r) => s + (r.ia?.sum ?? 0), 0);
  const max = 15 * n;
  return {
    n, rows, max,
    cols: { user: cols('user'), ia: hasIa ? cols('ia') : null },
    total: {
      user: { sum: userTotal, tier: totalTier(userTotal, n), pct: max ? Math.round((userTotal / max) * 100) : 0 },
      ia: hasIa ? { sum: iaTotal, tier: totalTier(iaTotal, n), pct: max ? Math.round((iaTotal / max) * 100) : 0 } : null,
    },
    withFiles: rated.filter(d => d.files > 0).length,
  };
}
export type DiffMetrics = ReturnType<typeof diffMetrics>;

/* ── a leitura do agente ──────────────────────────────────────────── */

const nota = z.number().int().min(1).max(5);
const pct = z.number().int().min(0).max(100);
export const diferenciaisReadingSchema = z.object({
  /** a trinca que as evidências sustentam, por diferencial */
  items: z.array(z.object({
    id: z.string().min(1).max(48),
    real: nota, distinct: nota, hard: nota,
    leitura: z.string().trim().max(240).default(''),
  })).max(12),
  /** consistência por critério, 0–100 */
  consistencia: z.object({ realidade: pct, distincao: pct, dificuldade: pct }),
  evidencias: z.object({ score: pct, leitura: z.string().trim().max(360).default('') }),
  fortes: z.array(z.string().trim().min(3).max(200)).max(4).default([]),
  riscos: z.array(z.string().trim().min(3).max(200)).max(4).default([]),
  recomendacoes: z.array(z.string().trim().min(3).max(200)).max(4).default([]),
});
export type DiferenciaisReading = z.infer<typeof diferenciaisReadingSchema>;

export const evidenceWord = (score: number) => (score >= 85 ? 'Ótima' : score >= 65 ? 'Boa' : score >= 45 ? 'Regular' : 'Fraca');
export type DiferenciaisReadingDto = ToolReadingDto<DiferenciaisReading>;

/** Os diferenciais que entram na leitura do agente: com nome e as três notas. */
export const diffReadable = (c: Diferenciais) => c.items.filter(d => d.label.trim() && diffRated(d));
